"use client";

import clsx from "clsx";
import { Fragment, useId, useState } from "react";
import {
  ICON_SIZES,
  Icon,
  type IconNameExtended,
} from "@/components/library/Icon";

export type ProductCapabilityCard = {
  icon: IconNameExtended;
  title: string;
  description: string;
};

export type ProductCapabilityTab = {
  id: string;
  label: string;
  cards: ProductCapabilityCard[];
};

export type MarketingProductCapabilitiesProps = {
  /** Section title. Wrap the emphasized phrase in `<em>` tags to render it in italic serif. */
  title: string;
  subtitle: string;
  tabs: ProductCapabilityTab[];
  className?: string;
};

function renderTitle(title: string) {
  return title.split(/<em>|<\/em>/).map((part, index) =>
    index % 2 === 1 ? (
      <span
        key={`${part}-${index}`}
        className="font-martina font-normal italic text-gail-primary"
      >
        {part}
      </span>
    ) : (
      <Fragment key={`${part}-${index}`}>{part}</Fragment>
    ),
  );
}

export function MarketingProductCapabilities({
  title,
  subtitle,
  tabs,
  className,
}: Readonly<MarketingProductCapabilitiesProps>) {
  const baseId = useId();
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id ?? "");
  const activeTab =
    tabs.find((tab) => tab.id === activeTabId) ?? tabs[0] ?? null;

  if (!activeTab) {
    return null;
  }

  const plainTitle = title.replaceAll(/<\/?em>/g, "");

  return (
    <section className={clsx("w-full px-4 pb-16 md:pb-24", className)}>
      <div className="mx-auto w-full max-w-6xl">
        <div className="max-w-3xl">
          <h2 className="font-monument text-[2rem] font-medium leading-[1.12] tracking-tight text-black">
            {renderTitle(title)}
          </h2>
          <p className="mt-4 font-monument text-[1.75rem] leading-snug text-black/50">
            {subtitle}
          </p>
        </div>

        <div
          role="tablist"
          aria-label={plainTitle}
          className="mt-10 flex gap-8 overflow-x-auto border-b border-black/10 md:mt-12 md:gap-12"
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab.id;

            return (
              <button
                key={tab.id}
                id={`${baseId}-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`${baseId}-panel-${tab.id}`}
                onClick={() => setActiveTabId(tab.id)}
                className={clsx(
                  "-mb-px shrink-0 border-b-2 pb-3 font-monument text-base transition-colors md:text-lg",
                  isActive
                    ? "border-black font-medium text-black"
                    : "border-transparent text-black/40 hover:text-black/60",
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="mt-8 grid md:mt-10">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab.id;

            return (
              <div
                key={tab.id}
                id={`${baseId}-panel-${tab.id}`}
                role="tabpanel"
                aria-labelledby={`${baseId}-tab-${tab.id}`}
                aria-hidden={!isActive}
                className={clsx(
                  "col-start-1 row-start-1 grid grid-cols-1 items-stretch gap-4 transition-opacity duration-200 sm:grid-cols-2 lg:grid-cols-3",
                  isActive
                    ? "opacity-100"
                    : "pointer-events-none opacity-0",
                )}
              >
                {tab.cards.map((card) => (
                  <div
                    key={card.title}
                    className="flex h-full flex-col rounded-2xl border border-black/5 bg-black/[0.03] p-6"
                  >
                    <Icon
                      icon={card.icon}
                      size={ICON_SIZES.XL}
                      className="text-black"
                    />
                    <h3 className="mt-4 font-monument text-xl font-medium text-black">
                      {card.title}
                    </h3>
                    <p className="mt-1.5 font-monument text-base leading-relaxed text-black/50">
                      {card.description}
                    </p>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
